
import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, UserRound } from "lucide-react";
import { User as UserType } from "@/types";
import { cn } from "@/lib/utils";

// Make sure we're using reliable placeholder images from Unsplash
const PLACEHOLDER_IMAGES = [
  "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9?auto=format&fit=crop&w=150&h=150",
  "https://images.unsplash.com/photo-1535268647677-300dbf3d78d1?auto=format&fit=crop&w=150&h=150",
  "https://images.unsplash.com/photo-1501286353178-1ec881214838?auto=format&fit=crop&w=150&h=150",
  "https://images.unsplash.com/photo-1485833077593-4278bba3f11f?auto=format&fit=crop&w=150&h=150",
  "https://images.unsplash.com/photo-1441057206919-63d19fac2369?auto=format&fit=crop&w=150&h=150",
];

interface AvatarPlaceholderProps {
  user: UserType;
  className?: string;
  fallbackClassName?: string;
  size?: "sm" | "md" | "lg";
}

export const AvatarPlaceholder = ({
  user,
  className,
  fallbackClassName,
  size = "md",
}: AvatarPlaceholderProps) => {
  // Avoid direct references to rory.jpg or jack.jpg in any code paths
  // Filter out bad image URLs
  const isBadImageUrl = (url?: string): boolean => {
    if (!url) return true;
    return url.includes('rory.jpg') || 
           url.includes('jack.jpg') || 
           url.startsWith('/');
  };
  
  // Generate a consistent index for a user based on their ID
  const getImageIndexFromUserId = (userId: string): number => {
    const sumOfChars = userId
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return sumOfChars % PLACEHOLDER_IMAGES.length;
  };

  // Get a placeholder image URL for a user
  const getPlaceholderImage = (userId: string): string => {
    const index = getImageIndexFromUserId(userId);
    return PLACEHOLDER_IMAGES[index];
  };

  // Determine avatar size
  const sizeClasses = {
    sm: "h-6 w-6",
    md: "h-10 w-10",
    lg: "h-16 w-16",
  };
  
  // Check if user image URL is valid
  const imageUrl = !isBadImageUrl(user.imageUrl) ? user.imageUrl : undefined;

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      <AvatarFallback 
        className={cn(
          "bg-forest text-white",
          size === "sm" && "text-xs",
          size === "md" && "text-sm",
          size === "lg" && "text-xl",
          fallbackClassName
        )}
      >
        {imageUrl ? <UserRound className="h-4 w-4" /> : user.name?.charAt(0) || "?"}
      </AvatarFallback>
      {imageUrl ? (
        <AvatarImage src={imageUrl} alt={user.name || "User"} />
      ) : (
        // Always use a reliable placeholder when imageUrl is missing or invalid
        <AvatarImage src={getPlaceholderImage(user.id)} alt={user.name || "User"} />
      )}
    </Avatar>
  );
};

export default AvatarPlaceholder;
